import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { createQuotation, listQuotations, getQuotationPDF, updateQuotationStatus } from '../api/quotations';
import { searchProducts, listTopProducts, searchProductByBarcode } from '../api/products';

// Página simple para crear y listar presupuestos (quotations)
// Usa endpoints backend /api/quotations
// Minimal: cliente se ingresa por ID (puedes reemplazar por selector de clientes existente)

function CreateQuotationForm({ onCreated }) {
  const [customerName, setCustomerName] = useState('');
  const [items, setItems] = useState([]);
  const [sending, setSending] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productFocused, setProductFocused] = useState(false);
  const productDebounceRef = useRef();
  const productBoxRef = useRef(null);

  const updateItem = (idx, field, value) => {
    setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const addManualItem = () => setItems([...items, { descripcion: '', cantidad: 1, precioUnitario: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const subtotalPreview = items.reduce((acc, it) => acc + (Number(it.cantidad || 0) * Number(it.precioUnitario || 0)), 0);

  // Buscar productos con debounce
  useEffect(() => {
    // Si no está enfocado, ocultar opciones y no pedir fallback
    if (!productFocused) { setProductOptions([]); return; }
    if (!productSearch || productSearch.length < 2) {
      // fallback sólo si enfocado
      (async ()=>{ setProductLoading(true); const top = await listTopProducts(8); setProductOptions(top); setProductLoading(false); })();
      return;
    }
    if (productDebounceRef.current) clearTimeout(productDebounceRef.current);
    productDebounceRef.current = setTimeout(async () => {
      setProductLoading(true);
      try {
        const results = await searchProducts(productSearch);
        setProductOptions(results);
      } catch (e) {
        setProductOptions([]);
      } finally { setProductLoading(false); }
    }, 300);
    return () => productDebounceRef.current && clearTimeout(productDebounceRef.current);
  }, [productSearch, productFocused]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    function handleClick(e){
      if (!productBoxRef.current) return;
      if (!productBoxRef.current.contains(e.target)) {
        setProductFocused(false);
        setProductOptions([]);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const clearCustomer = () => { setCustomerName(''); };

  const handleSelectProduct = (p) => {
    if (!p) return;
    // Precio robusto: usar salePrice, si no existe intentar costPrice + margen
    let price = Number(p.salePrice);
    if (!price || Number.isNaN(price)) {
      const cost = Number(p.costPrice) || 0;
      const margin = Number(p.margin) || 0;
      price = Math.round((cost * (1 + margin / 100)) * 100) / 100;
    }
    setItems(prev => {
      const existingIdx = prev.findIndex(x => x.productId === p._id);
      if (existingIdx >= 0) {
        const copy = [...prev];
        copy[existingIdx].cantidad = Number(copy[existingIdx].cantidad || 1) + 1;
        // Si el precio estaba en 0 y ahora tenemos uno válido, actualizarlo
        if (!copy[existingIdx].precioUnitario) copy[existingIdx].precioUnitario = price;
        return copy;
      }
      return [...prev, { descripcion: p.name || p.descripcion || 'Producto', cantidad: 1, precioUnitario: price || 0, productId: p._id }];
    });
    setProductSearch('');
    setProductOptions([]);
  };

  const tryBarcodeAdd = async () => {
    const raw = productSearch.trim();
    if (!raw) return;
    // Código de barras NUMÉRICO únicamente (dígitos) con longitud mínima >= 6
    const isLikelyBarcode = /^[0-9]+$/.test(raw) && raw.length >= 6;
    if (!isLikelyBarcode) return; // dejar que el flujo normal haga búsqueda textual
    const prod = await searchProductByBarcode(raw);
    if (prod) {
      handleSelectProduct(prod);
      // Verificar que se agregó realmente
      setTimeout(() => {
        // confirmación visual si no apareció
        const found = items.find(it => it.productId === (prod._id));
        if (!found) {
          toast.warn('Producto recuperado pero no visible, reintentando');
          handleSelectProduct(prod); // segundo intento
        } else {
          toast.success('Producto agregado por código');
        }
      }, 50);
    } else {
      toast.warn('Producto no encontrado por código');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!customerName.trim()) { toast.error('Ingresa nombre de cliente'); return; }
    if (!items.length) { toast.error('Agregar items'); return; }
    setSending(true);
    try {
      const payload = {
        customerName: customerName.trim(),
        items: items.map(it => ({ descripcion: it.descripcion || 'Item', cantidad: Number(it.cantidad)||1, precioUnitario: Number(it.precioUnitario)||0 }))
      };
      const res = await createQuotation(payload);
      toast.success('Presupuesto creado');
      onCreated && onCreated(res.quotation);
      setItems([]);
      clearCustomer();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Error creando presupuesto');
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ border: '1px solid #ddd', padding: '16px', borderRadius: '10px', background: '#fff', maxWidth: 520 }}>
      <h2 style={{ marginTop: 0 }}>Nuevo Presupuesto</h2>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Cliente (Nombre libre)</label>
        <input
          value={customerName}
          onChange={e => setCustomerName(e.target.value)}
          placeholder="Nombre del cliente"
          style={{ width: '100%', padding: '8px', marginTop: 4, borderRadius: 6, border: '1px solid #ccc' }}
          required
        />
      </div>
      <div style={{ marginBottom: 12, position:'relative' }} ref={productBoxRef}>
        <label style={{ fontSize:13, fontWeight:600 }}>Agregar productos</label>
        <input
          value={productSearch}
          onChange={e => setProductSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); tryBarcodeAdd(); } }}
          onFocus={()=> setProductFocused(true)}
          onBlur={()=> { /* esperar a click en opción -> manejado por mousedown global */ setTimeout(()=>{ if(!productFocused) setProductOptions([]); },120); }}
          placeholder="Buscar producto por nombre / código (Enter para agregar por código)"
          style={{ width:'100%', padding:'8px', marginTop:4, borderRadius:6, border:'1px solid #ccc' }}
        />
        {productFocused && (productOptions.length > 0 || productLoading || productSearch.length >=2) && (
          <div style={{ position:'absolute', zIndex:10, top:'100%', left:0, right:0, background:'#fff', border:'1px solid #ccc', borderRadius:6, marginTop:4, maxHeight:260, overflowY:'auto', boxShadow:'0 2px 6px rgba(0,0,0,0.15)' }}>
            {productLoading && <div style={{ padding:8, fontSize:12 }}>Buscando...</div>}
            {!productLoading && productOptions.map(p => (
              <div key={p._id} onClick={()=>handleSelectProduct(p)} style={{ padding:'6px 10px', cursor:'pointer', fontSize:12, display:'flex', justifyContent:'space-between', borderBottom:'1px solid #f2f2f2' }}>
                <span>{p.name}</span>
                <span style={{ color:'#0a7', fontWeight:600 }}>${Number(p.salePrice).toFixed(2)}</span>
              </div>
            ))}
            {!productLoading && productOptions.length === 0 && <div style={{ padding:8, fontSize:12 }}>Sin resultados</div>}
            {!productLoading && productSearch.length < 2 && <div style={{ padding:8, fontSize:11, color:'#555' }}>Teclea al menos 2 caracteres para refinar búsqueda</div>}
          </div>
        )}
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Ítems</label>
        {items.map((it, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input
              placeholder="Descripción"
              value={it.descripcion}
              onChange={e => updateItem(idx, 'descripcion', e.target.value)}
              style={{ flex: 2, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
              required
            />
            <input
              type="number"
              min={1}
              value={it.cantidad}
              onChange={e => updateItem(idx, 'cantidad', e.target.value)}
              style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
            />
            <input
              type="number"
              step="0.01"
              value={it.precioUnitario}
              onChange={e => updateItem(idx, 'precioUnitario', e.target.value)}
              style={{ width: 100, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
            />
            <button type="button" onClick={() => removeItem(idx)} disabled={items.length === 1} style={{ background: '#f44336', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>✕</button>
          </div>
        ))}
        <button type="button" onClick={addManualItem} style={{ marginTop: 8, background: '#1976d2', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}>Agregar ítem manual</button>
      </div>
      <div style={{ fontSize: 13, marginBottom: 12 }}>Subtotal estimado: <strong>${subtotalPreview.toFixed(2)}</strong></div>
      <button disabled={sending} style={{ background: '#28a745', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Guardar</button>
    </form>
  );
}

function QuotationList({ refreshKey }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [convertingId, setConvertingId] = useState(null);
  const [refSearch, setRefSearch] = useState('');

  const load = React.useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listQuotations(refSearch ? { reference: refSearch } : {});
      // API returns array directly
      setData(res.items ? res.items : Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e.message || 'Error cargando');
    } finally { setLoading(false); }
  }, [refSearch]);

  useEffect(() => { load(); }, [refreshKey, load]);

  const downloadPDF = async (id) => {
    try {
      const blob = await getQuotationPDF(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `presupuesto_${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || 'Error PDF');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await updateQuotationStatus(id, status);
      toast.success(`Estado: ${status}`);
      load();
    } catch (e) {
      toast.error(e.message || 'Error estado');
    }
  };

  const handleLoadIntoSales = async (q) => {
    if (!q) return;
    if (q.status !== 'accepted') { toast.warn('Debe estar aceptado'); return; }
    if (q.validityDate && new Date(q.validityDate) < new Date()) { toast.error('Presupuesto vencido'); return; }
    setConvertingId(q._id);
    try {
      // Guardar ID en localStorage para que Sales.jsx lo lea o usar query param directamente
      // Preferimos navegación con query param sin prefetch para reutilizar lógica centralizada
      window.location.href = `/sales?quotationId=${encodeURIComponent(q._id)}`;
    } catch (e) {
      toast.error(e.message || 'Error cargando en ventas');
      setConvertingId(null);
    }
  };

  if (loading) return <div style={{ padding: 20 }}>Cargando presupuestos...</div>;
  if (error) return <div style={{ padding: 20, color: 'red' }}>Error: {error}</div>;

  return (
    <div style={{ marginTop: 24 }}>
      <h2 style={{ marginTop: 0, fontSize:20, fontWeight:700, display:'flex', alignItems:'center', gap:8 }}>
        <span style={{ display:'inline-block', width:6, height:24, background:'#495057', borderRadius:4 }}></span>
        Listado de Presupuestos
      </h2>
      <div style={{ marginBottom: 16, display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
        <input
          value={refSearch}
          onChange={e => setRefSearch(e.target.value)}
          placeholder="Buscar por referencia (ej: P-2025-000012)"
          style={{ padding:'10px 12px', border:'1px solid #ced4da', borderRadius:8, minWidth:260, fontSize:13, boxShadow:'inset 0 1px 2px rgba(0,0,0,0.05)' }}
        />
        <button type="button" onClick={()=> setRefSearch('')} style={btnStyle('#6c757d')}>Limpiar</button>
      </div>
      <div style={{ border:'1px solid #dee2e6', borderRadius:12, overflow:'hidden', boxShadow:'0 4px 12px rgba(0,0,0,0.06)', background:'#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing:0 }}>
          <thead>
            <tr style={{ background: 'linear-gradient(90deg,#f8f9fa,#e9ecef)' }}>
              <th style={thStyle}>Ref</th>
              <th style={thStyle}>Fecha</th>
              <th style={thStyle}>Cliente</th>
              <th style={thStyle}>Items</th>
              <th style={thStyle}>Total</th>
              <th style={thStyle}>Estado</th>
              <th style={{ ...thStyle, textAlign:'center' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.map((q,i) => {
              const rowBg = i % 2 === 0 ? '#ffffff' : '#f8f9fa';
              const expired = q.validityDate && new Date(q.validityDate) < new Date();
              const statusColor = q.status === 'accepted'
                ? { bg:'#d1e7dd', fg:'#0f5132', border:'#0f5132' }
                : q.status === 'rejected'
                  ? { bg:'#f8d7da', fg:'#842029', border:'#842029' }
                  : q.status === 'draft'
                    ? { bg:'#e2e3e5', fg:'#495057', border:'#6c757d' }
                    : expired
                      ? { bg:'#ffe8cc', fg:'#99582a', border:'#99582a' }
                      : { bg:'#e2e3e5', fg:'#495057', border:'#6c757d' };
              return (
                <tr key={q._id} style={{ background:rowBg, transition:'background .15s' }}
                  onMouseEnter={e=> e.currentTarget.style.background='#eef2f5'}
                  onMouseLeave={e=> e.currentTarget.style.background=rowBg}>
                  <td style={tdCell}>{q.referenceNumber || q._id}</td>
                  <td style={tdCell}>{new Date(q.createdAt).toLocaleDateString('es-AR')}</td>
                  <td style={{ ...tdCell, fontStyle: q.customerName ? 'normal':'italic', color: q.customerName ? '#212529':'#6c757d' }}>{q.customerName || '—'}</td>
                  <td style={tdCell}>{q.items?.length || 0}</td>
                  <td style={{ ...tdCell, fontWeight:600 }}>${Number(q.total).toFixed(2)}</td>
                  <td style={tdCell}>
                    <span style={{
                      display:'inline-block', padding:'4px 10px', borderRadius:20, fontSize:11, fontWeight:600,
                      background: statusColor.bg, color: statusColor.fg, border:`1px solid ${statusColor.border}`, letterSpacing:'0.3px'
                    }}>
                      {expired && q.status === 'accepted' ? 'expired' : q.status}
                    </span>
                  </td>
                  <td style={{ ...tdCell, textAlign:'center' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent:'center', flexWrap:'wrap' }}>
                      <button onClick={() => downloadPDF(q._id)} style={smallBtn('#0d6efd')}>PDF</button>
                      {q.status === 'draft' && (
                        <>
                          <button onClick={() => updateStatus(q._id, 'accepted')} style={smallBtn('#198754')}>Aceptar</button>
                          <button onClick={() => updateStatus(q._id, 'rejected')} style={smallBtn('#dc3545')}>Rechazar</button>
                        </>
                      )}
                      {q.status === 'accepted' && (
                        <button
                          disabled={convertingId === q._id}
                          onClick={() => handleLoadIntoSales(q)}
                          style={smallBtn('#6f42c1')}
                          title={expired ? 'Vencido' : 'Cargar en Ventas'}
                        >{convertingId === q._id ? 'Cargando...' : 'Cargar'}</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!data.length && <tr><td colSpan={7} style={{ padding: 18, textAlign:'center', fontSize:13, color:'#6c757d' }}>Sin presupuestos</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const btnStyle = (bg) => ({
  background: bg, color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12
});

const smallBtn = (bg) => ({
  background:bg, color:'#fff', border:'none', padding:'5px 10px', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600,
  boxShadow:'0 1px 2px rgba(0,0,0,0.15)', letterSpacing:'0.3px'
});

const thStyle = {
  textAlign:'left', padding:12, fontSize:12, fontWeight:700, color:'#343a40', borderBottom:'1px solid #dee2e6'
};
const tdCell = {
  padding:12, fontSize:12, color:'#212529', borderTop:'1px solid #edf0f2', verticalAlign:'middle'
};

export default function QuotationsPage() {
  const { user } = useSelector(state => state.auth);
  const [refreshKey, setRefreshKey] = useState(0);
  const handleCreated = () => setRefreshKey(k => k + 1);
  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto', fontFamily:'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Arial, sans-serif' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button
            onClick={() => window.history.back()}
            style={{ background:'#343a40', color:'#fff', border:'none', padding:'8px 14px', borderRadius:8, cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', gap:6, boxShadow:'0 2px 4px rgba(0,0,0,0.2)' }}
            title="Regresar"
          >
            ← Regresar
          </button>
          <h1 style={{ margin:0, fontSize:28, fontWeight:700, letterSpacing:'0.5px', background:'linear-gradient(90deg,#343a40,#495057)', WebkitBackgroundClip:'text', color:'transparent' }}>📄 Presupuestos</h1>
        </div>
        <div style={{ fontSize:12, color:'#555', fontWeight:500 }}>Gestión rápida de presupuestos</div>
      </div>
      {!user && <div>Inicia sesión para operar.</div>}
      {user && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'minmax(340px, 420px) 1fr', gap:32 }}>
            <div style={{ alignSelf:'start' }}>
              <CreateQuotationForm onCreated={handleCreated} />
            </div>
            <div>
              <QuotationList refreshKey={refreshKey} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
